import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Box,
  Alert,
  CircularProgress,
  Button,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  listGuestEligibility,
  updateGuestEligibility,
  CourseTypeGuestEligibilityDTO,
} from '../services/courseTypeGuestEligibilityService';

/**
 * System Admin: configure which course types are available for Try as Guest.
 */
const GuestEligibilityAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [list, setList] = useState<CourseTypeGuestEligibilityDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listGuestEligibility();
      setList(data);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load';
      setError(msg);
      if (typeof (e as { response?: { status?: number } })?.response?.status === 'number') {
        const status = (e as { response: { status: number } }).response.status;
        if (status === 403) setError('Only SYSTEM_ADMIN can access this page.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (courseType: string, eligible: boolean) => {
    setUpdating(courseType);
    setError(null);
    try {
      await updateGuestEligibility(courseType, eligible);
      setList((prev) =>
        prev.map((item) => (item.courseType === courseType ? { ...item, eligible } : item))
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to update');
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/dashboard')} sx={{ mb: 2 }}>
        Back to Dashboard
      </Button>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
        Try as Guest – Course type eligibility
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Only course types marked as eligible appear in the guest &quot;Quick try: pick a course type&quot; section.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 3 }}>
        {list.length === 0 ? (
          <Typography color="text.secondary">No course types found.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {list.map((item) => (
              <Box
                key={item.courseType}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography>
                  <strong>{item.displayName}</strong> ({item.courseType})
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={item.eligible}
                      onChange={(_, checked) => handleToggle(item.courseType, checked)}
                      disabled={updating === item.courseType}
                    />
                  }
                  label={item.eligible ? 'Available for Try as Guest' : 'Hidden from guests'}
                />
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default GuestEligibilityAdminPage;
